import { useState } from 'react';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/ui/design-system/components/ui/dialog';
import { Button } from '@/ui/design-system/components/ui/button';
import { Label } from '@/ui/design-system/components/ui/label';

function RevealKeyDialog({ open, onOpenChange, apiKey }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(apiKey);
            setCopied(true);
            toast.success('Copied to clipboard!', {
                icon: '✅',
                position: 'top-right',
            });
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Failed to copy to clipboard');
        }
    };

    const handleClose = (value) => {
        if (!value) {
            setCopied(false);
        }
        onOpenChange(value);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent onInteractOutside={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle>API Key Created Successfully</DialogTitle>
                    <DialogDescription className="text-pretty">
                        Make sure to copy your API key now. You won&apos;t be
                        able to see it again.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                    <div>
                        <p className="text-pretty text-sm font-semibold text-amber-800">
                            For security, this key is only shown once.
                        </p>
                        <p className="text-pretty text-sm text-amber-700">
                            Copy it now and store it safely.
                        </p>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="revealed-api-key">Your API Key</Label>
                    <div
                        id="revealed-api-key"
                        className="rounded-md border bg-gray-50 p-3"
                    >
                        <code className="break-all text-sm">{apiKey}</code>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <Button onClick={handleCopy} className="w-full">
                        {copied ? (
                            <>
                                <Check className="h-4 w-4" />
                                Copied!
                            </>
                        ) : (
                            <>
                                <Copy className="h-4 w-4" />
                                Copy to Clipboard
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => handleClose(false)}
                        className="w-full"
                    >
                        Done, I&apos;ve Saved It
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default RevealKeyDialog;
